"use client";

import { useEffect, useMemo, useState } from "react";
import { COUNTRIES, areasOf, citiesOf, findCountry, loadGeo, regionOfCity, regionsOf } from "@/lib/invoice/geo";
import { CURRENCIES } from "@/lib/invoice/utils";
import { Combobox, Field, Input } from "./ui";

const COUNTRY_OPTIONS = COUNTRIES.map(([code, name, phone, currency]) => ({ value: name, label: name, hint: [code, currency].filter(Boolean).join(" · ") }));

// Country → State / region → City → Area → Postcode, with dropdowns for every
// country in the world. Everything also accepts your own text.
// autoCurrency: picking a country fills the currency (and it stays editable).
export function AddressFields({ value, onChange, stateLabel, showArea = true, autoCurrency = false }) {
  const iso = findCountry(value.country)?.[0] || "";
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    let live = true;
    setGeo(null);
    if (iso) loadGeo(iso).then((g) => live && setGeo(g));
    return () => {
      live = false;
    };
  }, [iso]);

  const regions = useMemo(() => (iso ? regionsOf(iso, geo).map((r) => ({ value: r.name, label: r.name, hint: iso === "IN" ? `GST ${r.code}` : r.code })) : []), [iso, geo]);
  const cities = useMemo(() => (iso && geo ? citiesOf(iso, geo, value.state) : []), [iso, geo, value.state]);
  const areas = useMemo(() => (iso && geo ? areasOf(iso, geo, value.city) : []), [iso, geo, value.city]);

  const set = (patch) => onChange({ ...value, ...patch });
  const onCountry = (country) => {
    const row = findCountry(country);
    const patch = { country };
    if (row && row[0] !== iso) Object.assign(patch, { state: "", city: "", area: "" });
    if (autoCurrency && row && CURRENCIES[row[3]] && (!value.currency || findCountry(value.country)?.[3] === value.currency)) patch.currency = row[3];
    set(patch);
  };
  const onCity = (city) => {
    if (!value.state && iso && geo) {
      const state = regionOfCity(iso, geo, city);
      if (state) return set({ city, state });
    }
    set({ city });
  };

  const regionWord = iso === "IN" || iso === "US" || iso === "AU" || iso === "MY" ? "State" : iso === "CA" ? "Province" : iso === "GB" ? "Nation / region" : "State / region";

  return (
    <>
      <Field label="Address" className="sm:col-span-2">
        <Input value={value.address || ""} onChange={(e) => set({ address: e.target.value })} placeholder="Building, street" />
      </Field>
      <Field label="Country">
        <Combobox value={value.country || ""} onChange={onCountry} options={COUNTRY_OPTIONS} placeholder="Choose country…" testId="country" />
      </Field>
      <Field label={stateLabel || (iso === "IN" ? "State (GST place of supply)" : regionWord)} hint={iso && !geo ? "Loading…" : !iso ? "Choose the country first" : undefined}>
        <Combobox value={value.state || ""} onChange={(state) => set({ state })} options={regions} placeholder={regions.length ? `Choose from ${regions.length}` : "Type…"} testId="region" />
      </Field>
      <Field label="City / town">
        <Combobox
          value={value.city || ""}
          onChange={onCity}
          options={cities}
          placeholder={cities.length ? `Choose from ${cities.length.toLocaleString()}` : iso && geo && !value.state ? "Pick the region first, or type" : "Type…"}
          testId="city"
        />
      </Field>
      {showArea && (
        <Field label={iso === "GB" ? (/^london$/i.test(value.city || "") ? "London borough / area" : "County / area") : "Area / locality"}>
          <Combobox value={value.area || ""} onChange={(area) => set({ area })} options={areas} placeholder={areas.length ? `Choose from ${areas.length}` : "Type…"} testId="area" />
        </Field>
      )}
      <Field label={iso === "US" ? "ZIP code" : iso === "IN" ? "PIN code" : "Postcode"}>
        <Input value={value.postcode || ""} onChange={(e) => set({ postcode: e.target.value })} />
      </Field>
    </>
  );
}
